import { Injectable } from '@angular/core';
import moment from 'moment';
import { RequestParamsType } from '@monsido/ng2/external/http/options';

export type StatisticsDateRange = {
    startDate?: moment.Moment;
    endDate?: moment.Moment;
};

type StatisticsListParamsInput = {
    domainId: number | string;
    dateRange: StatisticsDateRange;
    dateFormat: string;
    page: number;
    limit: number | string;
    search?: string;
    sortBy?: string;
    sortDirection?: string;
};

type StatisticsSetParamsPayload = {
    from?: moment.Moment;
    to?: moment.Moment;
    search?: string;
};

@Injectable({
    providedIn: 'root',
})
export class StatisticsParamsService {
    private static readonly URL_PROTOCOL_REGEX = /^https?:\/\//i;

    hasDateRange (dateRange: StatisticsDateRange): boolean {
        return Boolean(dateRange.startDate && dateRange.endDate);
    }

    buildSetParamsPayload (
        dateRange: StatisticsDateRange,
        search?: string,
    ): StatisticsSetParamsPayload {
        const payload: StatisticsSetParamsPayload = {};

        if (this.hasDateRange(dateRange)) {
            payload.from = moment(dateRange.startDate);
            payload.to = moment(dateRange.endDate);
        }

        if (search !== undefined) {
            payload.search = search;
        }

        return payload;
    }

    buildListParams (input: StatisticsListParamsInput): RequestParamsType {
        const {
            domainId,
            dateRange,
            dateFormat,
            page,
            limit,
            search,
            sortBy,
            sortDirection,
        } = input;

        const params: RequestParamsType = {
            limit,
            page: page || 1,
            domainId,
        };

        if (dateRange.startDate) {
            params.from = dateRange.startDate.format(dateFormat);
        }

        if (dateRange.endDate) {
            params.to = dateRange.endDate.format(dateFormat);
        }

        if (search !== undefined) {
            params.search = this.normalizeSearch(search);
        }

        if (sortBy) {
            params.sort_by = sortBy;
        }

        if (sortDirection) {
            params.sort_dir = sortDirection;
        }

        return params;
    }

    private normalizeSearch (search: string): string {
        const trimmedSearch = search.trim();

        if (!StatisticsParamsService.URL_PROTOCOL_REGEX.test(trimmedSearch)) {
            return search;
        }

        try {
            const parsedUrl = new URL(trimmedSearch);

            // Statistics URLs are indexed without dynamic query/hash fragments.
            return `${parsedUrl.origin}${parsedUrl.pathname}`;
        } catch {
            return search;
        }
    }

    buildComparisonParams (
        params: RequestParamsType,
        compareDateRange: StatisticsDateRange,
        dateFormat: string,
    ): RequestParamsType {
        const comparisonParams: RequestParamsType = {
            ...params,
        };

        if (compareDateRange.startDate) {
            comparisonParams.from = compareDateRange.startDate.format(dateFormat);
        }

        if (compareDateRange.endDate) {
            comparisonParams.to = compareDateRange.endDate.format(dateFormat);
        }

        return comparisonParams;
    }

    buildSummaryParams (
        domainId: number | string,
        dateRange: StatisticsDateRange,
        dateFormat: string,
        type: string = 'total',
    ): RequestParamsType {
        const params: RequestParamsType = {
            domainId,
            type,
        };

        if (dateRange.startDate) {
            params.from = dateRange.startDate.format(dateFormat);
        }

        if (dateRange.endDate) {
            params.to = dateRange.endDate.format(dateFormat);
        }

        return params;
    }
}
